import { Injectable } from '@angular/core';
import {
  DraftCreatedMetricsRequest,
  DraftEventDetails,
  DraftRetrievedMetricsRequest,
  FlowStartMetricsRequest,
  FlowSubmitMetricsRequest,
  IMetricsTrackingData,
  InheritanceStartMetricsRequest,
  NewAccountStartMetricsRequest,
  ProcessEndStateCodeEnum,
  ProcessPageTypeCodeEnum,
  SubmitMetricsResponse,
  SuccessfulSubmitMetricsRequest,
  TrackFailureMetricsRequest,
} from '@interfaces';
import { FundingMethodEnum } from '@interfaces/open-account/enums';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  AccountTypes,
  flowMapper,
  FlowTypes,
  GiftingDesignationTypes,
  GiftRecipientTypes,
  TransactionTypesEnum,
} from '@shared/models';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import { SubmitMetricsService } from '@shared/services/submit-metrics/submit-metrics.service';
import {
  HydrateDraftSuccess,
  SaveNewDraftSuccess,
  submitMetricsFlowStart,
  submitMetricsFlowSubmit,
  submitMetricsMultiFlowSubmit,
  submitMetricsOAEnd,
  submitMetricsOAStart,
  submitMetricsOASuccess,
  submitMetricUpdateProcessingId,
} from '@store/actions';
import {
  selectDraftId,
  selectSaveDraftEnabledForFlow,
} from '@store/selectors/draft/draft.selectors';
import { selectMXMState } from '@store/selectors/mxm/mxm.selectors';
import {
  selectAccountFromIRAReceipt,
  selectAccountFromNonretirementReceipt,
  selectAccountToIRAReceipt,
  selectAccountToNonretirementReceipt,
} from '@store/selectors/receipt/inheritance/inheritance-receipt.selectors';
import { selectAllTransactions } from '@store/selectors/transaction-state/transaction-state.selectors';
import { CookieService } from 'ngx-cookie-service';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { selectOAFromStore } from 'src/app/shared/services/open-account/oa.selectors';

import * as fromRootStore from '../../index';
import {
  selectedClientAccountsState,
  selectGiftingState,
  selectInternalTransferState,
  selectIsIRASingleExecutor,
  selectIsSingleTrustee,
  selectTriageState,
} from '../../index';
import { getOpenAccountState } from '../../reducers';
import { selectSubmitMetricsID } from '../../selectors/open-account/open-account.selectors';
import { selectClientSegment } from '../../selectors/personal-data/personal-data.selectors';

// Flows whose draft events are registered in the OBE schema. Add a flow here to opt it in.
const DRAFT_EVENT_FLOWS: FlowTypes[] = [FlowTypes.MINOR];

@Injectable()
export class SubmitMetricsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private submitMetricsService: SubmitMetricsService,
    private cookieService: CookieService,
    private loggerService: LoggerService,
    private windowRef: WindowRef,
  ) {}

  // mergeMap, not switchMap: SaveNewDraftSuccess has two dispatch sites, and a second save
  // must not cancel the first event's POST.
  submitMetricsDraftCreated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SaveNewDraftSuccess),
        withLatestFrom(
          this.store$.pipe(select(selectTriageState)),
          this.store$.pipe(select(selectSaveDraftEnabledForFlow)),
          this.store$.pipe(select(selectClientSegment)),
        ),
        filter(
          ([, triageState, isSaveDraftEnabled]) =>
            DRAFT_EVENT_FLOWS.includes(triageState.flowType) && isSaveDraftEnabled,
        ),
        mergeMap(([action, triageState, , clientSegment]) => {
          const submitMetricsData: DraftCreatedMetricsRequest = {
            journeyName: 'change-of-ownership',
            communicationChannel: 'web',
            version: '1.0',
            eventName: 'draft-created',
            correlationId: this.cookieService.get('submitMetricsProcessingId') || '',
            details: this.buildDraftEventDetails(
              triageState.flowType,
              action.draftId,
              clientSegment,
            ),
          };

          return this.callDraftMetrics(submitMetricsData);
        }),
      ),
    { dispatch: false },
  );

  submitMetricsDraftRetrieved$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HydrateDraftSuccess),
        withLatestFrom(
          this.store$.pipe(select(selectTriageState)),
          this.store$.pipe(select(selectSaveDraftEnabledForFlow)),
          this.store$.pipe(select(selectClientSegment)),
        ),
        filter(
          ([, triageState, isSaveDraftEnabled]) =>
            DRAFT_EVENT_FLOWS.includes(triageState.flowType) && isSaveDraftEnabled,
        ),
        mergeMap(([action, triageState, , clientSegment]) => {
          const submitMetricsData: DraftRetrievedMetricsRequest = {
            journeyName: 'change-of-ownership',
            communicationChannel: 'web',
            version: '1.0',
            eventName: 'draft-retrieved',
            correlationId: this.cookieService.get('submitMetricsProcessingId') || '',
            details: this.buildDraftEventDetails(
              triageState.flowType,
              action.draftId,
              clientSegment,
            ),
          };

          return this.callDraftMetrics(submitMetricsData);
        }),
      ),
    { dispatch: false },
  );

  submitMetricsOAStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitMetricsOAStart),
      withLatestFrom(
        this.store$.pipe(select(selectClientSegment)),
        this.store$.pipe(select(selectSubmitMetricsID)),
      ),
      switchMap(([, clientSegment, metricsID]) => {
        const submitMetricsData: InheritanceStartMetricsRequest | NewAccountStartMetricsRequest = {
          journeyName: 'change-of-ownership',
          communicationChannel: 'web',
          version: '2.0',
          eventName: clientSegment ? 'new-account-start' : 'initial-start',
          correlationId: clientSegment ? metricsID : '',
          details: {
            processPageType: ProcessPageTypeCodeEnum.OverviewPage,
            newOaUniqueStart: true,
            flowType: ProcessPageTypeCodeEnum.COOFlowTypeCode,
            segmentType: clientSegment ? clientSegment : 'PR',
          },
        };

        return this.submitMetricsService.callSubmitMetrics(submitMetricsData).pipe(
          tap((data) => {
            // if correlation we don't need to reset the cookie
            if (!metricsID) {
              data?.correlationId &&
                this.cookieService.set(
                  'submitMetricsProcessingId',
                  data.correlationId,
                  undefined,
                  '/',
                  '.vanguard.com',
                  true,
                  'None',
                );
            }
          }),
          filter<SubmitMetricsResponse, IMetricsTrackingData>(
            (data): data is IMetricsTrackingData => !!data?.correlationId && !metricsID,
          ),
          map((payload) => submitMetricUpdateProcessingId({ payload })),
        );
      }),
    ),
  );

  submitMetricsOASuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitMetricsOASuccess),
        withLatestFrom(
          this.store$.pipe(select(getOpenAccountState)),
          this.store$.pipe(select(selectSubmitMetricsID)),
          this.store$.pipe(select(selectClientSegment)),
          this.store$.pipe(select(selectOAFromStore)),
        ),
        switchMap(([, openAccountState, metricsID, clientSegment, selectedAccount]) => {
          const submitMetricsData: SuccessfulSubmitMetricsRequest = {
            journeyName: 'change-of-ownership',
            eventName: 'successful-submit',
            communicationChannel: 'web',
            correlationId: metricsID,
            version: '2.0',
            details: {
              processPageType: ProcessPageTypeCodeEnum.Confirm,
              processEndState: ProcessEndStateCodeEnum.LiteReg,
              confirmationNumber: openAccountState.openAccountResponse.confirmationNumber ?? '',
              newOaUniqueStart: false,
              paymentMethod: FundingMethodEnum.None,
              accountType: selectedAccount.selectedAccountTypeId,
              newAccount: !openAccountState.openAccountResponse.isS24Reject,
              expectedDollarAmount: '0',
              s24RuleFailId: openAccountState.openAccountResponse.isS24Reject
                ? openAccountState.openAccountResponse.failedId
                : undefined,
              flowType: ProcessPageTypeCodeEnum.COOFlowTypeCode,
              segmentType: clientSegment,
            },
          };
          this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
          return this.submitMetricsService.callSubmitMetrics(submitMetricsData);
        }),
      ),
    { dispatch: false },
  );

  submitMetricsOAEnd$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitMetricsOAEnd),
        withLatestFrom(
          this.store$.pipe(select(selectSubmitMetricsID)),
          this.store$.pipe(select(selectClientSegment)),
          this.store$.pipe(select(selectOAFromStore)),
        ),
        switchMap(([action, metricsID, clientSegment, selectedAccount]) => {
          const submitMetricsData: TrackFailureMetricsRequest = {
            journeyName: 'change-of-ownership',
            eventName: 'track-failure',
            correlationId: metricsID,
            communicationChannel: 'web',
            version: '2.0',
            details: {
              processPageType: action.payload,
              processEndState: ProcessEndStateCodeEnum.KickToLegacy,
              newOaUniqueStart: false,
              accountType: selectedAccount.selectedAccountTypeId,
              flowType: ProcessPageTypeCodeEnum.COOFlowTypeCode,
              segmentType: clientSegment,
            },
          };
          this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
          return this.submitMetricsService.callSubmitMetrics(submitMetricsData);
        }),
      ),
    { dispatch: false },
  );

  submitMetricsFlowStarted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitMetricsFlowStart),
      withLatestFrom(this.store$.pipe(select(selectTriageState))),
      switchMap(([, triageState]) => {
        const submitMetricsData: FlowStartMetricsRequest = {
          journeyName: 'change-of-ownership',
          communicationChannel: 'web',
          version: '1.0',
          eventName: 'flow-started',
          details: {
            flowName: triageState.flowType,
            transferType: triageState.flowType,
          },
        };

        return this.submitMetricsService.callSubmitMetrics(submitMetricsData).pipe(
          tap((data) => {
            data?.correlationId &&
              this.cookieService.set(
                'submitMetricsProcessingId',
                data.correlationId,
                undefined,
                '/',
                '.vanguard.com',
                true,
                'None',
              );
          }),
          filter<SubmitMetricsResponse, IMetricsTrackingData>(
            (data): data is IMetricsTrackingData => !!data?.correlationId,
          ),
          mergeMap((payload) => [
            submitMetricUpdateProcessingId({ payload }),
            fromRootStore.TrackProcessStart({
              processType: `coo-${flowMapper[triageState.flowType]}-flow`,
              processTrackingId: payload.correlationId,
            }),
          ]),
        );
      }),
    ),
  );

  submitMetricsMultiFlowSubmit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitMetricsMultiFlowSubmit),
        withLatestFrom(
          this.store$.pipe(select(selectSubmitMetricsID)),
          this.store$.pipe(select(selectAllTransactions)),
        ),
        switchMap(([, metricsID, allTransactions]) => {
          const metricsRequests: Observable<SubmitMetricsResponse>[] = [];

          if (!allTransactions || allTransactions.length === 0) {
            this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
            this.store$.dispatch(
              submitMetricUpdateProcessingId({ payload: { correlationId: '' } }),
            );
            return of([]);
          }

          for (const transaction of allTransactions) {
            const submitMetricsData: FlowSubmitMetricsRequest = {
              journeyName: 'change-of-ownership',
              communicationChannel: 'web',
              correlationId: metricsID,
              version: '1.0',
              eventName: 'flow-submitted',
              details: {
                flowName: FlowTypes.MULTI_INHERITANCE,
                transferType: FlowTypes.MULTI_INHERITANCE,
                fromRegistrationType:
                  transaction.selectedClientAccounts.transferFrom.selectedAccounts[0]
                    ?.accountType || '',
                toRegistrationType:
                  transaction.selectedClientAccounts.transferTo.selectedAccount.accountType || '',
                fromAccountNumber:
                  transaction.selectedClientAccounts.transferFrom.selectedAccounts[0]
                    ?.accountNumber || '',
                toAccountNumber:
                  transaction.selectedClientAccounts.transferTo.selectedAccount.accountNumber,
                newAccountFlag:
                  transaction.selectedClientAccounts.transactionType ===
                    TransactionTypesEnum.NEW_ACCOUNT ||
                  transaction.selectedClientAccounts.transactionType ===
                    TransactionTypesEnum.FORCED_NEW_ACCOUNT,
              },
            };

            metricsRequests.push(this.submitMetricsService.callSubmitMetrics(submitMetricsData));
          }

          // Execute all metrics requests in parallel and handle completion
          return forkJoin(metricsRequests).pipe(
            tap({
              next: () => {
                this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
                this.store$.dispatch(
                  submitMetricUpdateProcessingId({ payload: { correlationId: '' } }),
                );
              },
              error: () => {
                this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
                this.store$.dispatch(
                  submitMetricUpdateProcessingId({ payload: { correlationId: '' } }),
                );
              },
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  submitMetricsFlowSubmit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitMetricsFlowSubmit),
        withLatestFrom(
          this.store$.pipe(select(selectTriageState)),
          this.store$.pipe(select(selectIsIRASingleExecutor)),
          this.store$.pipe(select(selectIsSingleTrustee)),
          this.store$.pipe(select(getOpenAccountState)),
          this.store$.pipe(select(selectGiftingState)),
          this.store$.pipe(select(selectInternalTransferState)),
          this.store$.pipe(select(selectedClientAccountsState)),
          this.store$.pipe(select(selectSubmitMetricsID)),
          this.store$.pipe(select(selectMXMState)),
          this.store$.pipe(select(selectAccountFromIRAReceipt(false))),
          this.store$.pipe(select(selectAccountFromNonretirementReceipt(false))),
          this.store$.pipe(select(selectAccountToIRAReceipt(false))),
          this.store$.pipe(select(selectAccountToNonretirementReceipt(false))),
          this.store$.pipe(select(selectDraftId)),
        ),
        switchMap(
          ([
            action,
            triageState,
            isIRASingleExecutor,
            isSingleTrustee,
            openAccountState,
            giftingState,
            internalTransferState,
            selectedAccounts,
            metricsID,
            mxmState,
            accountFromIraReceipt,
            accountFromNonretirementReceipt,
            accountToIraReceipt,
            accountToNonretirementReceipt,
            draftId,
          ]) => {
            const transactionId = action.payload;
            const manipulatedID = Number(transactionId.replace('|', ''));
            const flowType = triageState.flowType;
            const internalGift =
              triageState.giftingDesignation === GiftingDesignationTypes.VANGUARD;
            const isNewAccount =
              internalTransferState.transactionType === TransactionTypesEnum.NEW_ACCOUNT ||
              selectedAccounts.transactionType === TransactionTypesEnum.NEW_ACCOUNT;
            let multiSigner = false;
            let transferValue: string | undefined = '';
            let accountFromNumber = '';
            let accountFromRegistration: string | undefined = '';
            let accountToNumber = '';
            let accountToRegistration: string | undefined = '';
            let isAppianSubmit = false;

            if (!isNaN(manipulatedID)) {
              isAppianSubmit = true;
            }

            // Sets values for the variables established above based on the flow type
            switch (flowType) {
              case FlowTypes.GIFTING:
                accountFromNumber = selectedAccounts.transferFrom.selectedAccounts[0].accountNumber;
                accountFromRegistration =
                  selectedAccounts.transferFrom.selectedAccounts[0].accountType;
                accountToNumber = giftingState.recipient.accountNumber;
                accountToRegistration = giftingState.recipient.type;
                transferValue = giftingState.totalGiftAmount.replace(/[$,]/g, '');
                multiSigner = mxmState.signState !== null && mxmState.transferId !== null;
                break;
              case FlowTypes.MINOR:
                accountFromNumber = selectedAccounts.transferFrom.selectedAccounts[0].accountNumber;
                accountFromRegistration =
                  selectedAccounts.transferFrom.selectedAccounts[0].accountType;
                transferValue =
                  selectedAccounts.transferFrom.selectedAccounts[0].accountData.balance;
                if (isNewAccount) {
                  accountToNumber = openAccountState.openAccountResponse.brokerageAccountNumber;
                  accountToRegistration = AccountTypes.INDV;
                } else {
                  accountToNumber = selectedAccounts.transferTo.selectedAccount.accountNumber;
                  accountToRegistration = selectedAccounts.transferTo.selectedAccount.accountType;
                }
                break;
              case FlowTypes.INHERITANCE:
                accountFromRegistration =
                  triageState.accountTypeInheriting === AccountTypes.IRA
                    ? accountFromIraReceipt.rows[0].values[0]
                    : accountFromNonretirementReceipt.rows[0].values[0];
                accountToRegistration =
                  triageState.accountTypeInheriting === AccountTypes.IRA
                    ? accountToIraReceipt.rows[0].values[0]
                    : accountToNonretirementReceipt.rows[0].values[0];
                if (isNewAccount) {
                  accountToNumber = openAccountState.openAccountResponse.brokerageAccountNumber;
                }
                if (!isNewAccount && !isIRASingleExecutor && !isSingleTrustee) {
                  accountToNumber = selectedAccounts.transferTo.selectedAccount.accountNumber;
                }
                break;
              case FlowTypes.INTERNAL_TRANSFER:
                accountFromNumber =
                  internalTransferState.transferFrom.selectedAccount.accountNumber;
                accountFromRegistration =
                  internalTransferState.transferFrom.selectedAccount.accountType;
                if (isNewAccount) {
                  accountToNumber = openAccountState.openAccountResponse.brokerageAccountNumber;
                  accountToRegistration = AccountTypes.INDV;
                } else {
                  accountToNumber = internalTransferState.transferTo.selectedAccount.accountNumber;
                  accountToRegistration = internalTransferState.triage.transferTo;
                }
                transferValue = internalTransferState.transferFrom.totalTransferValue;
                multiSigner = mxmState.signState !== null && mxmState.transferId !== null;
                break;
              case FlowTypes.DIVORCE:
                accountFromNumber =
                  internalTransferState.transferFrom.selectedAccount.accountNumber;
                accountFromRegistration =
                  internalTransferState.transferFrom.selectedAccount.accountType;
                accountToNumber = internalTransferState.transferTo.selectedAccount.accountNumber;
                accountToRegistration = internalTransferState.triage.transferTo;
                transferValue = internalTransferState.transferFrom.totalTransferValue;
                break;
            }

            const submitMetricsData: FlowSubmitMetricsRequest = {
              journeyName: 'change-of-ownership',
              communicationChannel: 'web',
              correlationId: metricsID,
              version: '1.0',
              eventName: 'flow-submitted',
              details: {
                flowName: flowType,
                transferType: flowType,
                // Omit when null — the schema rejects a null draftId.
                ...(draftId && { draftId }),
                stpProcessingId: !isAppianSubmit ? transactionId : '',
                appianCaseId: isAppianSubmit ? transactionId : '',
                ...(flowType !== FlowTypes.INHERITANCE && { dollarAmount: transferValue }),
                fromRegistrationType: accountFromRegistration ?? '',
                toRegistrationType: accountToRegistration ?? '',
                fromAccountNumber: accountFromNumber,
                toAccountNumber: accountToNumber,
                isCharityOrganization: giftingState.recipient.type === GiftRecipientTypes.CHARITY,
                isInternalGift: internalGift,
                multiSigner: multiSigner,
                confirmationNumber: isNewAccount
                  ? openAccountState.openAccountResponse.confirmationNumber
                  : '',
                newAccountFlag: isNewAccount,
                costBasisOptIn: (
                  internalTransferState.transferFrom.convertAvgCostHoldings ||
                  internalTransferState.transferFrom.convertSpecIDHoldings
                ).toString(),
              },
            };
            return this.submitMetricsService.callSubmitMetrics(submitMetricsData).pipe(
              tap({
                next: () => {
                  this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
                  this.store$.dispatch(
                    submitMetricUpdateProcessingId({ payload: { correlationId: '' } }),
                  );
                },
                error: () => {
                  this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
                  this.store$.dispatch(
                    submitMetricUpdateProcessingId({ payload: { correlationId: '' } }),
                  );
                },
              }),
            );
          },
        ),
      ),
    { dispatch: false },
  );

  private buildDraftEventDetails(
    flowType: FlowTypes,
    draftId: string,
    clientSegment: string,
  ): DraftEventDetails {
    return {
      flowName: flowType,
      transferType: flowType,
      draftId,
      segmentTypeCode: clientSegment ? clientSegment : 'PR',
      createdOnUrl: this.windowRef.nativeWindow.location.href,
    };
  }

  // Log and swallow so a failed event never breaks the draft. The `obe-*-error` eventIds are
  // wired to Splunk alerts — keep them in sync with the backend's event-id.type.ts.
  private callDraftMetrics(
    submitMetricsData: DraftCreatedMetricsRequest | DraftRetrievedMetricsRequest,
  ): Observable<SubmitMetricsResponse> {
    return this.submitMetricsService.callSubmitMetrics(submitMetricsData).pipe(
      catchError((error) => {
        this.loggerService.error(
          {
            message: `Failed to publish ${submitMetricsData.eventName} OBE event`,
            eventId: `obe-${submitMetricsData.eventName}-error`,
            error: error?.message,
            data: { draftId: submitMetricsData.details.draftId },
          },
          LoggerCodes.OBE_DRAFT_EVENT_FAILURE,
        );
        return EMPTY;
      }),
    );
  }
}
